// Description: Lower part of the sidebar that have the logout and configuration options
// Updated: 01/10/2025
// Author: Simon Franklin

// React
import * as React from 'react';

// Material UI Components
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// Components and Functions for the project
import SelectContent from './SelectContent';
import MenuContent from './MenuContent';
import CardAlert from './CardAlert';
import OptionsMenu from './OptionsMenu';
import use123zapStore from '../../context/123zapStore';
import { jwtDecode } from 'jwt-decode';

const drawerWidth = 300;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

// Render The Component
export default function SideMenu() {

  const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
  const showSidebar = use123zapStore((state) => state.showSidebar);
  const setShowSidebar = use123zapStore((state) => state.setShowSidebar);
  const userInfo = use123zapStore((state) => state.userInfo);
  const finishedRegister = use123zapStore((state) => state.finishedRegister);
  const setFinishedRegister = use123zapStore((state) => state.setFinishedRegister);

  const getInitials = (name) => { 
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase();
  };

  const location = window.location;

  React.useEffect(() => {
    const token = localStorage.getItem('idToken');

    if (userAuthenticated === true) {
      const decodedToken = jwtDecode(token);

      if (!decodedToken['custom:complete_profile']) {
        setFinishedRegister(false);
      } else {
        setFinishedRegister(true);
      }

    }

  }, [userAuthenticated]);

  React.useEffect(() => {

    const restrictedRoutes = ["/planos", "/planos/carrinho", "/finalizar-cadastro", "/verificar-email", "/canais/adicionar/whatsapp", "/canais/adicionar/whatsapp-business", "/canais/adicionar/sms", "/canais/adicionar/email"];

    if (restrictedRoutes.includes(location.pathname) && userAuthenticated) {
      setShowSidebar(false);
    } else {
      setShowSidebar(true);
    }
  }, [location.pathname, userAuthenticated]);

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: showSidebar ? 'block' : 'none' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          p: 1.5,
        }}
      >
        <SelectContent />
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MenuContent />
      </Box>
      {finishedRegister === false ? <CardAlert /> : null}
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Avatar
          sizes="small"
          alt=""
          sx={{ width: 36, height: 36, backgroundColor: 'primary.main', color: 'white' }}
        >
          {getInitials(userInfo?.name || userInfo?.username || 'U')}
        </Avatar>
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }}>
            {
              (userInfo?.NAME ? userInfo?.NAME.length > 15 ? userInfo?.NAME.substring(0, 12) + '...' : userInfo?.NAME
                : userInfo?.username ? userInfo?.username.length > 15 ? userInfo?.username.substring(0, 12) + '...' : userInfo?.username
                  : 'Usuário')
            }
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {userInfo?.email || ""}
          </Typography>
        </Box>
        <OptionsMenu />
      </Stack>
    </Drawer>
  );
}
